import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { Activity, Coins, Hash, Image, DollarSign, UserCheck, TrendingUp } from 'lucide-react';
import {
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyStatCard,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import adminUsageService, {
  UsageOverviewResponse,
  PeriodType,
} from '../../../services/api/adminUsageService';

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF6B6B', '#4ECDC4', '#45B7D1'
];

const PERIOD_OPTIONS: { value: PeriodType; label: string }[] = [
  { value: 'day', label: '今日' },
  { value: 'week', label: '過去7日間' },
  { value: 'month', label: '過去30日間' },
  { value: 'year', label: '過去1年間' },
];

const UsageOverviewDashboard: React.FC = () => {
  const [data, setData] = useState<UsageOverviewResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodType>('month');

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminUsageService.getOverview(period);
      if (response.success) {
        setData(response.data);
      } else {
        setError('データの取得に失敗しました');
      }
    } catch (err) {
      console.error('Failed to fetch usage overview:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatTooltipNumber = (value: unknown): string => {
    const numericValue = typeof value === 'number' ? value : Number(value);
    return formatNumber(Number.isFinite(numericValue) ? numericValue : 0);
  };

  const creditTooltipFormatter = (value: unknown): [React.ReactNode, React.ReactNode] => [
    formatTooltipNumber(value),
    'クレジット',
  ];

  const activationRateTooltipFormatter = (value: unknown): [React.ReactNode, React.ReactNode] => {
    const numericValue = typeof value === 'number' ? value : Number(value);
    return [
      `${(Number.isFinite(numericValue) ? numericValue : 0).toFixed(1)}%`,
      '有効化率',
    ];
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
        <TaskySpinner size="md" />
        読み込み中...
      </div>
    );
  }

  if (error) {
    return <TaskyErrorState description={error} />;
  }

  if (!data) {
    return <TaskyEmptyState title="データがありません" />;
  }

  return (
    <div>
      {/* Period Selector */}
      <div className="inline-flex items-center gap-1 bg-white rounded-tasky-sm border border-warm-border p-1 mb-6">
        {PERIOD_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setPeriod(option.value)}
            className={`px-3 py-1.5 rounded-tasky-xs text-sm transition-colors ${
              period === option.value
                ? 'bg-primary text-white font-medium'
                : 'text-gray-600 hover:bg-warm-hover'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <TaskyStatCard
          label="総リクエスト数"
          value={formatNumber(data.summary.total_requests)}
          icon={<Activity />}
          iconTone="primary"
        />
        <TaskyStatCard
          label="Taskyクレジット"
          value={formatNumber(data.summary.total_credits || 0)}
          icon={<Coins />}
          iconTone="success"
        />
        <TaskyStatCard
          label="総トークン数"
          value={formatNumber(data.summary.total_tokens)}
          icon={<Hash />}
          iconTone="purple"
        />
        <TaskyStatCard
          label="画像生成数"
          value={formatNumber(data.summary.total_image_count)}
          icon={<Image />}
          iconTone="warning"
        />
        <TaskyStatCard
          label="推定コスト"
          value={`$${data.summary.total_cost_usd.toFixed(2)}`}
          icon={<DollarSign />}
          iconTone="destructive"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Daily Trend Chart */}
        <div className="bg-white rounded-tasky-md border border-warm-border p-6 lg:col-span-2">
          <h3 className="text-sm font-semibold text-dark mb-5">日別クレジット使用量</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.daily_trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatNumber}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(label) => new Date(label).toLocaleDateString('ja-JP')}
                formatter={creditTooltipFormatter}
              />
              <Area
                type="monotone"
                dataKey="total_credits"
                stroke="#0088FE"
                fill="#0088FE"
                fillOpacity={0.3}
                name="クレジット"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Feature Usage Pie Chart */}
        <div className="bg-white rounded-tasky-md border border-warm-border p-6">
          <h3 className="text-sm font-semibold text-dark mb-5">機能別使用量</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.by_feature as unknown as Record<string, unknown>[]}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="total_credits"
                nameKey="feature_label"
                label={({ name, percent }: { name?: string; percent?: number }) =>
                  `${name || ''} (${((percent || 0) * 100).toFixed(0)}%)`
                }
                labelLine={false}
              >
                {data.by_feature.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={creditTooltipFormatter}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Organization Usage Bar Chart */}
        <div className="bg-white rounded-tasky-md border border-warm-border p-6">
          <h3 className="text-sm font-semibold text-dark mb-5">組織別使用量（Top 10）</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={data.by_organization}
              layout="vertical"
              margin={{ left: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                tickFormatter={formatNumber}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                type="category"
                dataKey="organization_name"
                tick={{ fontSize: 12 }}
                width={100}
              />
              <Tooltip
                formatter={creditTooltipFormatter}
              />
              <Bar
                dataKey="total_credits"
                fill="#00C49F"
                name="クレジット"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.activation_funnel && (
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TaskyStatCard
              label="登録ユーザー"
              value={formatNumber(data.activation_funnel.summary.signup_users)}
              icon={<UserCheck />}
              iconTone="primary"
            />
            <TaskyStatCard
              label="有効化ユーザー"
              value={formatNumber(data.activation_funnel.summary.activated_users)}
              icon={<TrendingUp />}
              iconTone="success"
            />
            <TaskyStatCard
              label="有効化率"
              value={`${data.activation_funnel.summary.activation_rate.toFixed(1)}%`}
              delta={data.activation_funnel.summary.largest_dropoff_step
                ? `最大離脱: ${data.activation_funnel.summary.largest_dropoff_step.label} ${data.activation_funnel.summary.largest_dropoff_step.dropoff_from_previous.toFixed(1)}%`
                : '離脱データなし'}
              deltaTone="neutral"
              icon={<Activity />}
              iconTone="warning"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-tasky-md border border-warm-border p-6">
              <h3 className="text-sm font-semibold text-dark mb-5">登録後ファネル</h3>
              <div className="space-y-3">
                {data.activation_funnel.funnel_steps.map((step) => (
                  <div key={step.key}>
                    <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                      <span className="font-medium text-dark">{step.label}</span>
                      <span className="text-gray-500">{formatNumber(step.users)}人 / {step.rate.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${Math.max(0, Math.min(100, step.rate))}%` }}
                      />
                    </div>
                    {step.dropoff_from_previous > 0 && (
                      <p className="mt-1 text-xs text-gray-400">前ステップから {step.dropoff_from_previous.toFixed(1)}% 離脱</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-tasky-md border border-warm-border p-6">
              <h3 className="text-sm font-semibold text-dark mb-5">機能到達</h3>
              <TaskyTable>
                <TaskyTableHead>
                  <TaskyTableRow hoverable={false}>
                    <TaskyTableHeaderCell>機能</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell align="right">到達ユーザー</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell align="right">到達率</TaskyTableHeaderCell>
                  </TaskyTableRow>
                </TaskyTableHead>
                <TaskyTableBody>
                  {data.activation_funnel.feature_reach.map((feature) => (
                    <TaskyTableRow key={feature.key}>
                      <TaskyTableCell className="font-medium">{feature.label}</TaskyTableCell>
                      <TaskyTableCell align="right">{formatNumber(feature.users)}</TaskyTableCell>
                      <TaskyTableCell align="right">{feature.rate.toFixed(1)}%</TaskyTableCell>
                    </TaskyTableRow>
                  ))}
                </TaskyTableBody>
              </TaskyTable>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-tasky-md border border-warm-border p-6">
              <h3 className="text-sm font-semibold text-dark mb-5">登録コホート別有効化率</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.activation_funnel.cohort_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(value) => `${value}%`} tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={(label) => new Date(label).toLocaleDateString('ja-JP')}
                    formatter={activationRateTooltipFormatter}
                  />
                  <Bar dataKey="activation_rate" fill="#0088FE" name="有効化率" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-tasky-md border border-warm-border p-6">
              <h3 className="text-sm font-semibold text-dark mb-5">組織別登録後有効化</h3>
              <TaskyTable>
                <TaskyTableHead>
                  <TaskyTableRow hoverable={false}>
                    <TaskyTableHeaderCell>組織</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell align="right">登録</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell align="right">有効化</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell align="right">率</TaskyTableHeaderCell>
                  </TaskyTableRow>
                </TaskyTableHead>
                <TaskyTableBody>
                  {data.activation_funnel.by_organization.map((org) => (
                    <TaskyTableRow key={org.organization_id ?? 'none'}>
                      <TaskyTableCell className="font-medium">{org.organization_name}</TaskyTableCell>
                      <TaskyTableCell align="right">{formatNumber(org.signup_users)}</TaskyTableCell>
                      <TaskyTableCell align="right">{formatNumber(org.activated_users)}</TaskyTableCell>
                      <TaskyTableCell align="right">{org.activation_rate.toFixed(1)}%</TaskyTableCell>
                    </TaskyTableRow>
                  ))}
                </TaskyTableBody>
              </TaskyTable>
            </div>
          </div>
        </div>
      )}

      {/* Feature Usage Table */}
      <div>
        <h3 className="text-sm font-semibold text-dark mb-3">機能別詳細</h3>
        <TaskyTable>
          <TaskyTableHead>
            <TaskyTableRow hoverable={false}>
              <TaskyTableHeaderCell>機能</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">リクエスト数</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">クレジット</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">トークン数</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">画像生成数</TaskyTableHeaderCell>
              <TaskyTableHeaderCell align="right">推定コスト</TaskyTableHeaderCell>
            </TaskyTableRow>
          </TaskyTableHead>
          <TaskyTableBody>
            {data.by_feature.map((feature, index) => (
              <TaskyTableRow key={index}>
                <TaskyTableCell className="font-medium">{feature.feature_label}</TaskyTableCell>
                <TaskyTableCell align="right">{formatNumber(feature.total_requests)}</TaskyTableCell>
                <TaskyTableCell align="right">{formatNumber(feature.total_credits || 0)}</TaskyTableCell>
                <TaskyTableCell align="right">{formatNumber(feature.total_tokens)}</TaskyTableCell>
                <TaskyTableCell align="right">{formatNumber(feature.total_image_count)}</TaskyTableCell>
                <TaskyTableCell align="right">${feature.total_cost_usd.toFixed(4)}</TaskyTableCell>
              </TaskyTableRow>
            ))}
          </TaskyTableBody>
        </TaskyTable>
      </div>
    </div>
  );
};

export default UsageOverviewDashboard;
